import { formatDateTime, formatTime } from '../../../utils/dateTimeFormat';

export const RANGE_OPTIONS = [
  { id: '24h', label: '24h' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: 'custom', label: 'Custom' },
];

export const MAX_CUSTOM_DAYS = 90;

export const RANGE_LABEL = {
  '24h': 'previous 24h',
  '7d': 'previous 7 days',
  '30d': 'previous 30 days',
  custom: 'previous period',
};

export const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

export const fmtCount = (v) => (isNum(v) ? v.toLocaleString() : '—');

export const fmtPct = (v) => (isNum(v) ? `${Math.round(v * 10) / 10}%` : '—');

export function fmtDuration(seconds) {
  if (!isNum(seconds)) return '—';
  if (seconds < 60) return `${Math.round(seconds * 10) / 10}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

/** Server timestamps are UTC; naive values (no offset) are treated as UTC. */
export function toDate(value) {
  if (!value) return null;
  const text = String(value);
  const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(text);
  const d = new Date(hasZone ? text : `${text}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function fmtDateTime(value) {
  const d = toDate(value);
  if (!d) return '—';
  return formatDateTime(d);
}

export function fmtRelative(value, now = Date.now()) {
  const d = toDate(value);
  if (!d) return '—';
  const diff = Math.round((now - d.getTime()) / 1000);
  if (diff < 45) return 'just now';
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.round(diff / 86400)}d ago`;
  return fmtDateTime(value);
}

/** Bucket keys are UTC: "YYYY-MM-DD" (daily) or "YYYY-MM-DDTHH" (hourly). */
export function bucketLabel(key, granularity) {
  if (!key) return '';
  if (granularity === 'hour') {
    const d = new Date(`${key}:00:00Z`);
    return Number.isNaN(d.getTime()) ? key : formatTime(d);
  }
  const d = new Date(`${key}T00:00:00Z`);
  return Number.isNaN(d.getTime())
    ? key
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** Text for a KPI comparison. Returns null when the API marked it non-comparable. */
export function comparisonText(kpi, { unit = 'count' } = {}) {
  if (!kpi?.comparable || !isNum(kpi.delta)) return null;
  if (kpi.delta === 0) return '→ no change';
  const arrow = kpi.delta > 0 ? '↑' : '↓';
  const abs = Math.abs(kpi.delta);
  if (unit === 'pct') return `${arrow} ${Math.round(abs * 10) / 10} pts`;
  if (unit === 'seconds') return `${arrow} ${fmtDuration(abs)}`;
  return `${arrow} ${abs.toLocaleString()}`;
}

export const RUN_OUTCOME = {
  passed: { label: 'Passed', tone: 'success' },
  failed: { label: 'Failed', tone: 'danger' },
  aborted: { label: 'Aborted', tone: 'warning' },
  running: { label: 'Running', tone: 'info' },
  unknown: { label: 'Unknown', tone: 'neutral' },
  never_run: { label: 'Never run', tone: 'neutral' },
};

/** Maps an outcome tone to a status string the shared StatusBadge colours correctly. */
export const BADGE_STATUS = { success: 'passed', danger: 'failed', info: 'running', warning: 'warning', neutral: 'neutral' };

export function runPath(run) {
  if (!run) return 'executions';
  const q = new URLSearchParams({ type: run.type, entity_id: String(run.entity_id ?? ''), name: run.name || '' });
  return `executions/${run.id}?${q.toString()}`;
}
